import { driver, type DriveStep } from 'driver.js';
import { ref, onBeforeUnmount, nextTick } from 'vue';

import 'driver.js/dist/driver.css';
import { getOnboardingSteps, type GuideStep } from '@/types/onboarding';

export function useOnboardingGuide() {
  const driverInstance = ref<ReturnType<typeof driver> | null>(null);
  const { t } = useI18n();

  const { mutate: setGuideAsSeen } = useUsersSetUserHaveSeenGuide();
  const authStore = useAuthStore();

  const steps = computed<GuideStep[]>(() => getOnboardingSteps(authStore));

  const start = async() => {
    await nextTick();
    const existingSteps = steps.value.filter(step => step.enable);
    if (existingSteps.length === 0) {
      console.warn('[Driver.js] No steps to show — all step targets were missing.');
      return;
    }

    driverInstance.value ??= driver({
      animate: true,
      stagePadding: 5,
      overlayOpacity: 0.75,
      allowClose: true,
      allowKeyboardControl: true,
      overlayClickBehavior: 'close',
      popoverClass: 'onboarding-popover',
      showButtons: ['next', 'previous', 'close'],
      doneBtnText: `${t('common.CTA.finish')} ✔`,
      nextBtnText: `${t('common.navigation.next')} ›`,
      prevBtnText: `‹ ${t('common.navigation.previous')}`,
      smoothScroll: true,
    });

    const formattedSteps: DriveStep[] = existingSteps.map(step => ({
      element: step.element,
      popover: {
        title: t(step.title),
        description: t(step.description),
        side: step.position ?? 'bottom',
        onNextClick: async() => {
          if (step.onNextNavigateTo) {
            await navigateTo(step.onNextNavigateTo);
          } else if (step.onNextHandler) {
            await step.onNextHandler();
          }
          driverInstance.value?.moveNext();
        },
      },
      onDeselected: () => {
        if (driverInstance.value?.isActive()) return;
        setGuideAsSeen();
      },
    }));

    driverInstance.value.setSteps(formattedSteps);
    driverInstance.value.drive(0);
  };

  const close = () => {
    driverInstance.value?.destroy();
    setGuideAsSeen();
  };

  onBeforeUnmount(() => {
    close();
  });

  return {
    driver: driverInstance,
    start,
    close,
  };
}
